// ═══════════════════════════════════════════════════════════════════════════
// FINANCIAL MODAL - UrbanQ v1.9.6
// Modal reutilizable para crear/editar ingresos y gastos
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { FiX, FiDollarSign, FiTrendingUp, FiTrendingDown } from 'react-icons/fi';
import './FinancialModal.css';

export default function FinancialModal({
    isOpen,
    onClose,
    onSave,
    type = 'income', // 'income' | 'expense'
    editData = null,
    categories = [],
    paymentMethods = ['Efectivo', 'Transferencia', 'Tarjeta', 'PayPal']
}) {
    const initialFormData = {
        date: new Date().toISOString().split('T')[0],
        description: '',
        category_id: '',
        subcategory_id: '',
        amount: '',
        payment_method: 'Transferencia',
        bank_account: '',
        reference: '',
        notes: '',
        expense_type: 'Variable',
        is_recurring: false
    };

    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [subcategories, setSubcategories] = useState([]);

    // Cargar datos al editar
    useEffect(() => {
        if (editData) {
            setFormData({
                date: editData.date ? editData.date.split('T')[0] : new Date().toISOString().split('T')[0],
                description: editData.description || '',
                category_id: editData.category_id || '',
                subcategory_id: editData.subcategory_id || '',
                amount: editData.amount || '',
                payment_method: editData.payment_method || 'Transferencia',
                bank_account: editData.bank_account || '',
                reference: editData.reference || '',
                notes: editData.notes || '',
                expense_type: editData.expense_type || 'Variable',
                is_recurring: editData.is_recurring || false
            });
        } else {
            setFormData(initialFormData);
        }
    }, [editData, isOpen]);

    // Cargar subcategorias cuando cambia la categoria
    useEffect(() => {
        if (formData.category_id) {
            const category = categories.find(c => c.id === parseInt(formData.category_id));
            if (category && category.subcategories) {
                setSubcategories(category.subcategories);
            } else {
                setSubcategories([]);
            }
        } else {
            setSubcategories([]);
        }
    }, [formData.category_id, categories]);

    const handleChange = (e) => {
        const { name, value, type: inputType, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: inputType === 'checkbox' ? checked : value
        }));

        // Limpiar error al cambiar
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.date) {
            newErrors.date = 'La fecha es requerida';
        }
        if (!formData.description.trim()) {
            newErrors.description = 'La descripcion es requerida';
        }
        if (!formData.category_id) {
            newErrors.category_id = 'Selecciona una categoria';
        }
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            newErrors.amount = 'Ingresa un monto valido';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        setLoading(true);
        try {
            await onSave({
                ...formData,
                amount: parseFloat(formData.amount),
                category_id: parseInt(formData.category_id),
                subcategory_id: formData.subcategory_id ? parseInt(formData.subcategory_id) : null,
                is_recurring: formData.is_recurring ? 1 : 0
            });
            onClose();
        } catch (error) {
            console.error('Error al guardar:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const isIncome = type === 'income';
    const modalTitle = editData
        ? (isIncome ? 'Editar Ingreso' : 'Editar Gasto')
        : (isIncome ? 'Nuevo Ingreso' : 'Nuevo Gasto');

    // Filtrar categorias por tipo
    const filteredCategories = categories.filter(c => c.type === type);

    return (
        <div className="urbanq-finanzas-modal-overlay" onClick={onClose}>
            <div className="urbanq-finanzas-modal" onClick={e => e.stopPropagation()}>
                <div className="urbanq-finanzas-modal-header">
                    <h3>
                        {isIncome ? <FiTrendingUp /> : <FiTrendingDown />}
                        {modalTitle}
                    </h3>
                    <button className="urbanq-finanzas-modal-close" onClick={onClose}>
                        <FiX />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="urbanq-finanzas-modal-body">
                        {/* Fecha y Monto */}
                        <div className="urbanq-finanzas-form-row">
                            <div className="urbanq-finanzas-form-group">
                                <label>
                                    Fecha <span className="required">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    className={`urbanq-finanzas-form-input ${errors.date ? 'error' : ''}`}
                                />
                                {errors.date && <span className="urbanq-finanzas-form-error">{errors.date}</span>}
                            </div>

                            <div className="urbanq-finanzas-form-group">
                                <label>
                                    Monto <span className="required">*</span>
                                </label>
                                <div className="urbanq-finanzas-amount-input">
                                    <span className="currency-symbol">$</span>
                                    <input
                                        type="number"
                                        name="amount"
                                        value={formData.amount}
                                        onChange={handleChange}
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                        className={`urbanq-finanzas-form-input ${errors.amount ? 'error' : ''}`}
                                    />
                                </div>
                                {errors.amount && <span className="urbanq-finanzas-form-error">{errors.amount}</span>}
                            </div>
                        </div>

                        {/* Descripcion */}
                        <div className="urbanq-finanzas-form-group">
                            <label>
                                Descripcion <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder={isIncome ? 'Ej: Salario mensual' : 'Ej: Pago de servicios'}
                                className={`urbanq-finanzas-form-input ${errors.description ? 'error' : ''}`}
                            />
                            {errors.description && <span className="urbanq-finanzas-form-error">{errors.description}</span>}
                        </div>

                        {/* Categoria y Subcategoria */}
                        <div className="urbanq-finanzas-form-row">
                            <div className="urbanq-finanzas-form-group">
                                <label>
                                    Categoria <span className="required">*</span>
                                </label>
                                <select
                                    name="category_id"
                                    value={formData.category_id}
                                    onChange={handleChange}
                                    className={`urbanq-finanzas-form-select ${errors.category_id ? 'error' : ''}`}
                                >
                                    <option value="">Seleccionar...</option>
                                    {filteredCategories.map(cat => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.category_id && <span className="urbanq-finanzas-form-error">{errors.category_id}</span>}
                            </div>

                            {!isIncome && subcategories.length > 0 && (
                                <div className="urbanq-finanzas-form-group">
                                    <label>Subcategoria</label>
                                    <select
                                        name="subcategory_id"
                                        value={formData.subcategory_id}
                                        onChange={handleChange}
                                        className="urbanq-finanzas-form-select"
                                    >
                                        <option value="">Ninguna</option>
                                        {subcategories.map(sub => (
                                            <option key={sub.id} value={sub.id}>
                                                {sub.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Metodo de pago y Cuenta */}
                        <div className="urbanq-finanzas-form-row">
                            <div className="urbanq-finanzas-form-group">
                                <label>Metodo de Pago</label>
                                <select
                                    name="payment_method"
                                    value={formData.payment_method}
                                    onChange={handleChange}
                                    className="urbanq-finanzas-form-select"
                                >
                                    {paymentMethods.map(method => (
                                        <option key={method} value={method}>{method}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="urbanq-finanzas-form-group">
                                <label>Cuenta/Banco</label>
                                <input
                                    type="text"
                                    name="bank_account"
                                    value={formData.bank_account}
                                    onChange={handleChange}
                                    placeholder="Ej: Banco Pichincha"
                                    className="urbanq-finanzas-form-input"
                                />
                            </div>
                        </div>

                        {/* Solo para gastos: Tipo y Recurrente */}
                        {!isIncome && (
                            <>
                                <div className="urbanq-finanzas-form-group">
                                    <label>Tipo de Gasto</label>
                                    <div className="urbanq-finanzas-type-selector">
                                        <div
                                            className={`urbanq-finanzas-type-option fijo ${formData.expense_type === 'Fijo' ? 'selected' : ''}`}
                                            onClick={() => setFormData(prev => ({ ...prev, expense_type: 'Fijo' }))}
                                        >
                                            Fijo
                                        </div>
                                        <div
                                            className={`urbanq-finanzas-type-option variable ${formData.expense_type === 'Variable' ? 'selected' : ''}`}
                                            onClick={() => setFormData(prev => ({ ...prev, expense_type: 'Variable' }))}
                                        >
                                            Variable
                                        </div>
                                    </div>
                                </div>

                                <div className="urbanq-finanzas-form-group">
                                    <div className="urbanq-finanzas-toggle-group">
                                        <div
                                            className={`urbanq-finanzas-toggle ${formData.is_recurring ? 'active' : ''}`}
                                            onClick={() => setFormData(prev => ({ ...prev, is_recurring: !prev.is_recurring }))}
                                        >
                                            <div className="urbanq-finanzas-toggle-knob"></div>
                                        </div>
                                        <span className="urbanq-finanzas-toggle-label">
                                            Gasto recurrente (se repite cada mes)
                                        </span>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Referencia (solo ingresos) */}
                        {isIncome && (
                            <div className="urbanq-finanzas-form-group">
                                <label>Referencia/Comprobante</label>
                                <input
                                    type="text"
                                    name="reference"
                                    value={formData.reference}
                                    onChange={handleChange}
                                    placeholder="Ej: Numero de transferencia"
                                    className="urbanq-finanzas-form-input"
                                />
                            </div>
                        )}

                        {/* Notas */}
                        <div className="urbanq-finanzas-form-group">
                            <label>Notas (opcional)</label>
                            <textarea
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                placeholder="Notas adicionales..."
                                className="urbanq-finanzas-form-textarea"
                                rows={3}
                            />
                        </div>
                    </div>

                    <div className="urbanq-finanzas-modal-footer">
                        <button
                            type="button"
                            className="urbanq-finanzas-modal-btn cancel"
                            onClick={onClose}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="urbanq-finanzas-modal-btn save"
                            disabled={loading}
                        >
                            {loading ? 'Guardando...' : (editData ? 'Actualizar' : 'Guardar')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
